import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiClient, Mitgliederliste, Projektmitglied, ProjektRechte } from '../api';
import {
  ConfirmDialog, ContentSection, DataTable, EmptyState, ErrorState, Feld, StatusBadge,
} from '../muster';
import type { Spalte } from '../muster';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PROJEKTRECHTE (PT-0a, Projekttrennung, Auftrag des Eigentümers F39)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Wer dieses Projekt sieht. SystemAdmin sieht immer jedes Projekt; sonst ein
 * Angemeldeter, wenn das Projekt „offen für alle Angemeldeten“ ist oder er
 * Mitglied ist. Bis zur Entscheidung des Eigentümers über die Rechte ist jedes
 * bestehende Projekt offen (Migration 1720) - niemand wird ausgesperrt.
 *
 * Die Karte steht nur da, wenn der Server `rechte.mitgliederVerwalten` meldet
 * (SystemAdmin oder ProjectAdmin mit Verwaltungsrecht). Der Server bleibt die
 * Sperre: Er prüft jede Änderung selbst und verlangt einen Grund.
 */
export function Projektrechte({ api, projektId, rechte, neuLaden }: {
  api: ApiClient;
  projektId: string;
  rechte?: ProjektRechte;
  neuLaden?: () => void;
}) {
  if (!rechte?.mitgliederVerwalten) return null;
  return <Mitgliederpflege api={api} projektId={projektId} neuLaden={neuLaden} />;
}

/** Fehlertext für die Anzeige; 403 in Alltagssprache. */
export function rechteFehlertext(e: unknown): string {
  const t = e instanceof Error ? e.message : String(e);
  return t === 'HTTP 403'
    ? 'Das dürfen nur SystemAdmin und ProjectAdmin mit Verwaltungsrecht in diesem Projekt.'
    : t;
}

function Mitgliederpflege({ api, projektId, neuLaden }: {
  api: ApiClient;
  projektId: string;
  neuLaden?: () => void;
}) {
  const [liste, setListe] = useState<Mitgliederliste | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [grund, setGrund] = useState('');
  const [meldung, setMeldung] = useState<string | null>(null);
  const [aktionsfehler, setAktionsfehler] = useState<string | null>(null);
  const [neu, setNeu] = useState('');
  const [neuVerwalten, setNeuVerwalten] = useState(false);
  const [entfernen, setEntfernen] = useState<Projektmitglied | null>(null);
  const [zugang, setZugang] = useState(false);

  const laden = useCallback(() => {
    setFehler(null);
    api.projektMitglieder(projektId)
      .then(setListe)
      .catch((e: unknown) => { setListe(null); setFehler(e); });
  }, [api, projektId]);

  useEffect(() => { laden(); }, [laden]);

  /** Jede Änderung braucht einen Grund - der Server lehnt sonst ab. */
  const mitGrund = useCallback(async (aktion: (g: string) => Promise<unknown>, erfolg: string): Promise<boolean> => {
    const g = grund.trim();
    setMeldung(null);
    if (!g) { setAktionsfehler('Bitte zuerst einen Grund angeben - er steht im Protokoll.'); return false; }
    setAktionsfehler(null);
    try {
      await aktion(g);
      setMeldung(erfolg);
      laden();
      neuLaden?.();
      return true;
    } catch (e) {
      setAktionsfehler(rechteFehlertext(e));
      return false;
    }
  }, [grund, laden, neuLaden]);

  const setzen = useCallback((m: Projektmitglied, canExport: boolean, canAdminister: boolean) =>
    mitGrund((g) => api.mitgliedSetzen(projektId, m.benutzerId, { canExport, canAdminister, grund: g }),
      `${m.displayName}: gespeichert.`), [api, projektId, mitGrund]);

  const spalten = useMemo<Spalte<Projektmitglied>[]>(() => [
    {
      schluessel: 'name', titel: 'Name', wert: (m) => m.displayName,
      inhalt: (m) => (
        <>
          {m.displayName}
          {!m.aktiv && <>{' '}<StatusBadge zustand="neutral" text="deaktiviert" titel="zählt nicht als Mitglied" klein /></>}
        </>
      ),
    },
    { schluessel: 'email', titel: 'E-Mail', wert: (m) => m.email },
    {
      schluessel: 'export', titel: 'Exporte', wert: (m) => (m.canExport ? 1 : 0), sortierbar: false,
      titelHinweis: 'Gespeichert, aber noch nicht ausgewertet (Entscheidung des Eigentümers).',
      inhalt: (m) => (
        <Feld art="haken" beschriftung="Exporte" wert={m.canExport}
          beiAenderung={(w) => { void setzen(m, w === true, m.canAdminister); }} />
      ),
    },
    {
      schluessel: 'verwalten', titel: 'Verwalten', wert: (m) => (m.canAdminister ? 1 : 0), sortierbar: false,
      titelHinweis: 'Mit der Rolle ProjectAdmin: darf die Mitglieder dieses Projekts pflegen.',
      inhalt: (m) => (
        <Feld art="haken" beschriftung="Verwalten" wert={m.canAdminister}
          beiAenderung={(w) => { void setzen(m, m.canExport, w === true); }} />
      ),
    },
    {
      schluessel: 'aktion', titel: '', sortierbar: false,
      inhalt: (m) => (
        <button className="m-knopf" data-entfernen={m.benutzerId} onClick={() => setEntfernen(m)}>Entfernen</button>
      ),
    },
  ], [setzen]);

  const offen = liste?.offenFuerAlle ?? false;
  const aktiveMitglieder = (liste?.mitglieder ?? []).filter((m) => m.aktiv).length;

  return (
    <ContentSection
      titel="Projektrechte"
      beschreibung={'Wer dieses Projekt sieht. SystemAdmin sieht jedes Projekt; sonst jeder Angemeldete, solange '
        + 'das Projekt offen ist, und die Mitglieder.'}
    >
      {fehler !== null && (
        <ErrorState klein meldung="Die Mitglieder konnten nicht geladen werden." technisch={fehler} beiErneutVersuchen={laden} />
      )}
      {meldung && <p className="hinweis" role="status" data-meldung>{meldung}</p>}
      {aktionsfehler && <p className="hinweis warnung" role="alert" data-aktionsfehler>{aktionsfehler}</p>}

      {liste && (
        <p data-zugang>
          <StatusBadge
            zustand={offen ? 'info' : 'gut'}
            text={offen ? 'offen für alle Angemeldeten' : 'nur Mitglieder und SystemAdmin'}
          />
          {liste.zugangSchalten && (
            <>
              {' '}
              <button className="m-knopf" data-zugang-schalten onClick={() => setZugang(true)}>
                {offen ? 'Auf Mitglieder beschränken …' : 'Für alle Angemeldeten öffnen …'}
              </button>
            </>
          )}
        </p>
      )}

      <Feld
        beschriftung="Grund der Änderung"
        wert={grund}
        beiAenderung={(w) => setGrund(w as string)}
        pflicht
        breite="voll"
        hinweis="Steht mit jeder Änderung im Protokoll. Ohne Grund ändert der Server nichts."
        platzhalter="z. B. neu im Projektteam Lüftung"
      />

      <DataTable
        spalten={spalten}
        zeilen={liste?.mitglieder ?? []}
        schluessel={(m) => m.benutzerId}
        laedt={liste === null && fehler === null}
        leer={(
          <EmptyState
            klein
            titel="Noch keine Mitglieder."
            erklaerung={offen
              ? 'Das Projekt ist offen - jeder Angemeldete sieht es.'
              : 'Nur SystemAdmin sieht dieses Projekt.'}
          />
        )}
      />

      {liste && (
        <div data-hinzufuegen>
          <Feld
            art="auswahl"
            beschriftung="Mitglied hinzufügen"
            wert={neu}
            beiAenderung={(w) => setNeu(w as string)}
            platzhalter={liste.bekannte.length ? 'Benutzer wählen' : 'Kein weiterer bekannter Benutzer'}
            optionen={liste.bekannte.map((b) => ({ wert: b.benutzerId, beschriftung: `${b.displayName} (${b.email})` }))}
            hinweis="Wählbar ist, wer sich schon einmal angemeldet hat."
          />
          <Feld art="haken" beschriftung="Verwalten" wert={neuVerwalten} beiAenderung={(w) => setNeuVerwalten(w === true)} />
          <button
            className="m-knopf"
            data-hinzufuegen-knopf
            disabled={!neu}
            onClick={() => {
              const b = liste.bekannte.find((x) => x.benutzerId === neu);
              if (!b) return;
              void mitGrund(
                (g) => api.mitgliedSetzen(projektId, b.benutzerId, { canExport: true, canAdminister: neuVerwalten, grund: g }),
                `${b.displayName} ist jetzt Mitglied.`,
              ).then((ok) => { if (ok) { setNeu(''); setNeuVerwalten(false); } });
            }}
          >
            Hinzufügen
          </button>
        </div>
      )}

      <ConfirmDialog
        offen={entfernen !== null}
        art="loeschen"
        titel={`${entfernen?.displayName ?? ''} aus dem Projekt entfernen?`}
        text={offen
          ? 'Solange das Projekt offen ist, sieht diese Person es weiter.'
          : 'Danach sieht diese Person das Projekt nicht mehr.'}
        beiBestaetigen={() => {
          const m = entfernen;
          setEntfernen(null);
          if (!m) return;
          void mitGrund((g) => api.mitgliedEntfernen(projektId, m.benutzerId, g), `${m.displayName} entfernt.`);
        }}
        beiAbbrechen={() => setEntfernen(null)}
      />

      <ConfirmDialog
        offen={zugang}
        art="warnung"
        titel={offen ? 'Projekt auf Mitglieder beschränken?' : 'Projekt für alle Angemeldeten öffnen?'}
        text={offen
          ? `Danach sehen nur noch ${aktiveMitglieder} aktive(s) Mitglied(er) und SystemAdmin dieses Projekt.`
          : 'Danach sieht jeder Angemeldete dieses Projekt.'}
        bestaetigenText={offen ? 'Beschränken' : 'Öffnen'}
        beiBestaetigen={() => {
          setZugang(false);
          void mitGrund((g) => api.projektZugangSetzen(projektId, !offen, g),
            offen ? 'Das Projekt ist jetzt auf Mitglieder beschränkt.' : 'Das Projekt ist jetzt offen.');
        }}
        beiAbbrechen={() => setZugang(false)}
      />
    </ContentSection>
  );
}
